import React, { ChangeEvent, useState } from 'react';
import {
  Gender,
  UpdateUserMutationVariables,
  User,
  useDisableAccountMutation,
} from '../../../shared/types/generated';
import { useAuth } from '../../auth/auth-hook';
import { useCallAction, useStore } from '@cobuildlab/react-simple-state';
import { updateUserAction } from '../../user/user-actions';
import { openDialogAlert } from '../../alert/alert-actions';
import {
  Box,
  Divider,
  Grid,
  TextField,
  Theme,
  Typography,
} from '@mui/material';
import { createStyles, makeStyles, styled } from '@mui/styles';
import { ButtonDefault } from '../../../shared/components/ButtonDefault';
import { USER_GENDER_LIST } from '../setting-utils';
import { SelectDefault } from '../../../shared/components/input/SelectDefault';
import { changeAvatarStore } from '../setting-events';
import { PRIMARY_COLOR } from '../../../shared/css/theme';
import { useNavigate } from 'react-router-dom';
import { changeEmailDialogStoreAction } from '../setting-actions';
import { changeEmailDialogStore } from '../setting-stores';
import { DialogDefault } from '../../../shared/components/dialog/DialogDefault';

const useStyles = makeStyles<Theme>(() =>
  createStyles({
    icon: {
      color: '#999999',
      fontSize: '20px!important',
      paddingRight: '5px',
    },
    input: {
      background: 'white',
      fontSize: '18px',
      height: '48px',
      fontFamily: 'manop !important',
    },
    changeEmailButton: {
      border: `1px solid ${PRIMARY_COLOR}!important`,
      color: `${PRIMARY_COLOR}!important`,
    },
  }),
);

const StyledButton = styled(ButtonDefault)({
  borderRadius: '6px!important',
  backgroundColor: `${PRIMARY_COLOR}!important`,
  border: 'none!important',
  color: 'white!important',
  height: '38px !important',
  '&:disabled': {
    backgroundColor: '#FF9EA1!important',
  },
  width: 200,
  fontFamily: 'oswald !important',
  textTransform: 'uppercase',
  fontWeight: '400 !important',
});
type ChangeEmailData = {
  email: string;
  password: string;
};
export const SettingAccountForm: React.FC = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { open } = useStore(changeEmailDialogStore);
  const { user, refetch } = useAuth();
  const { file } = useStore(changeAvatarStore);
  const [changeEmailData, setChangeEmailData] = useState<ChangeEmailData>({
    email: '',
    password: '',
  });
  const [userData, setUserData] = useState<User>({
    id: 0,
    name: '',
    password: '',
    gender: Gender.Female,
    email: '',
    birthday: '',
    ...user,
  });
  const [repeatPassword, setRepeatPassword] = useState<string>('');

  const [updateUser, loading] = useCallAction(updateUserAction, {
    onCompleted: () => {
      refetch();
      return openDialogAlert('Se ha actualizado tu perfil correctamente.');
    },
    onError: (error) => {
      return openDialogAlert(error.message);
    },
  });
  const [disableUser, { loading: loadingdisableUser }] =
    useDisableAccountMutation({
      onCompleted: () => {
        openDialogAlert(
          'Se ha eliminado tu perfil lamentamos tu partida.',
          () => navigate('/logout'),
        );
        return;
      },
      onError: (error) => {
        return openDialogAlert(error.message);
      },
    });

  const onChangeHandler = (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ): void => {
    setUserData({
      ...userData,
      [event.target.name as keyof User]: event.target.value as string,
    });
  };
  const onChangeHandlerEmail = (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ): void => {
    setChangeEmailData({
      ...changeEmailData,
      [event.target.name as keyof ChangeEmailData]: event.target
        .value as string,
    });
  };

  const handleUpdate = (): void => {
    if (userData.password && userData.password !== repeatPassword) {
      return openDialogAlert('Las contraseñas deben coincidir');
    }

    const { id, name, password, gender, birthday, site } = userData;
    let variables: UpdateUserMutationVariables = {
      data: {
        user: {
          id,
          name,
          gender,
          birthday,
          site,
        },
      },
    };

    if (password) {
      variables = {
        data: {
          user: { ...(variables.data?.user as User), password },
        },
      };
    }

    if (file) {
      variables = {
        data: {
          ...variables.data,
          file,
        },
      };
    }

    updateUser(variables);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TextField
          placeholder={'Nombre de usuario'}
          name="name"
          fullWidth
          value={decodeURI(userData.name)}
          InputLabelProps={{
            shrink: true,
            style: { fontSize: 19, fontFamily: 'manop' },
          }}
          variant="outlined"
          InputProps={{
            className: classes.input,
          }}
          onChange={(event) => onChangeHandler(event)}
        />
      </Grid>

      <Grid item xs={6}>
        <TextField
          placeholder="Contraseña"
          name="password"
          type="password"
          fullWidth
          value={userData.password as string}
          InputLabelProps={{
            shrink: true,
            style: { fontSize: 19, fontFamily: 'manop' },
          }}
          variant="outlined"
          InputProps={{
            className: classes.input,
          }}
          onChange={(event) => onChangeHandler(event)}
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          placeholder="Repetir Contraseña"
          name="repeatPassword"
          type="password"
          fullWidth
          value={repeatPassword}
          InputLabelProps={{
            shrink: true,
            style: { fontSize: 19, fontFamily: 'manop' },
          }}
          variant="outlined"
          InputProps={{
            className: classes.input,
          }}
          onChange={(event) => {
            setRepeatPassword(event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={6}>
        <SelectDefault
          placeholder="Sexo"
          variant="outlined"
          options={USER_GENDER_LIST}
          name={'gender'}
          defaultValue={user?.gender}
          InputLabelProps={{ style: { fontFamily: 'manop' } }}
          InputProps={{
            className: classes.input,
          }}
          onChangeData={(field, value) => {
            setUserData({
              ...userData,
              [field]: value as Gender,
            });
          }}
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          placeholder="Fecha de nacimiento"
          name="birthday"
          type="date"
          fullWidth
          value={userData?.birthday}
          InputLabelProps={{
            shrink: true,
            style: { fontSize: 19, fontFamily: 'manop' },
          }}
          variant="outlined"
          InputProps={{
            className: classes.input,
          }}
          onChange={(event) => {
            setUserData({
              ...userData,
              birthday: event.target.value as Gender,
            });
          }}
        />
      </Grid>
      <Grid xs={12}>
        <Box
          mt={'20px'}
          mb={'20px'}
          alignItems={'center'}
          justifyContent={'center'}
          display={'flex'}
          width={'full'}
        >
          <StyledButton isLoading={loading} onClick={() => handleUpdate()}>
            Guardar cambios
          </StyledButton>
        </Box>
      </Grid>

      <Grid xs={12}>
        <Box my={'10px'} paddingLeft={2}>
          <Divider style={{ marginBottom: '10px' }} />
          <Typography fontFamily={'manop'} marginBottom={2}>
            En cualquier momento si usted asi lo considera puede eliminar su
            cuenta. Toda su información será eliminada de nuestros servidores.
          </Typography>
          <StyledButton
            isLoading={loadingdisableUser}
            onClick={() => changeEmailDialogStoreAction(true)}
          >
            Eliminar cuenta
          </StyledButton>

          <DialogDefault
            isOpen={open}
            handleClose={() => changeEmailDialogStoreAction(false)}
            title="Eliminar cuenta en Animal Polítco."
            isLoading={loadingdisableUser}
            showActions
            handleSuccess={() => {
              disableUser({
                variables: {
                  data: {
                    email: changeEmailData.email,
                    password: changeEmailData.password,
                  },
                },
              });
            }}
          >
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <TextField
                  placeholder={'Correo electrónico'}
                  name="email"
                  fullWidth
                  value={changeEmailData?.email}
                  InputLabelProps={{
                    shrink: true,
                    style: { fontSize: 19 },
                  }}
                  variant="outlined"
                  InputProps={{
                    className: classes.input,
                  }}
                  onChange={(event) => onChangeHandlerEmail(event)}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  placeholder="Contraseña"
                  name="password"
                  type="password"
                  fullWidth
                  value={changeEmailData.password as string}
                  InputLabelProps={{
                    shrink: true,
                    style: { fontSize: 19 },
                  }}
                  variant="outlined"
                  InputProps={{
                    className: classes.input,
                  }}
                  onChange={(event) => onChangeHandlerEmail(event)}
                  helperText="Ingrese su contraseña para confirmar que deseas eliminar tu cuenta. Al hacer clic en el botón de confirmár, se eliminará permanentemente toda la información asociada a tu cuenta. Esta acción no se puede deshacer."
                />
              </Grid>
            </Grid>
          </DialogDefault>
        </Box>
      </Grid>
    </Grid>
  );
};
